var _excluded = ["disabled", "disableFirstOption", "errorMessage", "fullWidth", "fromFormat", "helperText", "helperTextPlacement", "label", "minutesIncrementStep", "onChange", "name", "required", "showError", "size", "toFormat", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Box from '../Box';
import FormControl from '../FormControl';
import Select from '../Select';
import { getHourOptions, getMeridianOptions, getMinuteOptions, parseTimeStrByFormat } from '../../utils/helpers';
var useStyles = makeStyles(function (theme) {
  return {
    container: {
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      '& .MuiNativeSelect-icon': {
        width: function width(_ref) {
          var size = _ref.size;
          return size === 'small' ? '0.7em' : null;
        },
        height: function height(_ref2) {
          var size = _ref2.size;
          return size === 'small' ? '0.7em' : null;
        },
        right: function right(_ref3) {
          var size = _ref3.size;
          return size === 'small' ? '3px' : null;
        },
        top: function top(_ref4) {
          var size = _ref4.size;
          return size === 'small' ? 'calc(50% - 9px)' : null;
        }
      }
    },
    select: {
      minWidth: function minWidth(_ref5) {
        var size = _ref5.size;
        return size === 'small' ? 50 : 80;
      },
      '& > select': {
        padding: '0.8rem 2rem 0.7rem 1rem !important'
      },
      '& > fieldset': {
        borderColor: function borderColor(_ref6) {
          var required = _ref6.required,
            value = _ref6.value;
          return required && !value ? theme.palette.secondary.main : null;
        }
      }
    },
    spacer: {
      color: theme.palette.default.light,
      padding: function padding(_ref7) {
        var size = _ref7.size;
        return size === 'small' ? '0 3px' : '0 5px';
      },
      fontSize: function fontSize(_ref8) {
        var size = _ref8.size;
        return size === 'small' ? '14px' : '18px';
      },
      fontWeight: 700
    }
  };
});
function TimeInput(_ref9) {
  var disabled = _ref9.disabled,
    disableFirstOption = _ref9.disableFirstOption,
    errorMessage = _ref9.errorMessage,
    fullWidth = _ref9.fullWidth,
    fromFormat = _ref9.fromFormat,
    helperText = _ref9.helperText,
    helperTextPlacement = _ref9.helperTextPlacement,
    label = _ref9.label,
    minutesIncrementStep = _ref9.minutesIncrementStep,
    onChange = _ref9.onChange,
    name = _ref9.name,
    required = _ref9.required,
    showError = _ref9.showError,
    size = _ref9.size,
    toFormat = _ref9.toFormat,
    value = _ref9.value,
    rest = _objectWithoutPropertiesLoose(_ref9, _excluded);
  var inputRef = useRef();
  var classes = useStyles({
    required: required,
    size: size,
    value: inputRef.current ? inputRef.current.value : value
  });
  var _useState = useState({
      hour: '',
      minute: '',
      meridian: ''
    }),
    values = _useState[0],
    setValues = _useState[1];

  // Set the initial values
  useEffect(function () {
    if (value) {
      var dateObj = parseTimeStrByFormat(value, fromFormat);
      setValues({
        hour: dateObj.format('hh'),
        minute: dateObj.format('mm'),
        meridian: dateObj.format('a')
      });
    }
  }, [fromFormat, value]);
  var hourOptions = getHourOptions(disableFirstOption);
  var meridianOptions = getMeridianOptions(disableFirstOption);
  var minuteOptions = getMinuteOptions(0, minutesIncrementStep === 5 ? 55 : 59, minutesIncrementStep, disableFirstOption);
  var handleChange = function handleChange(e, type) {
    var _extends2;
    var newValues = _extends({}, values, (_extends2 = {}, _extends2[type] = e.target.value, _extends2));
    var dateObj = parseTimeStrByFormat(newValues.hour + ":" + newValues.minute + newValues.meridian, 'hh:mma');
    var dateStr = dateObj.format(toFormat);
    var newValue = dateStr === 'Invalid Date' ? '' : dateStr;
    inputRef.current.value = newValue;
    var evt = new Event('change', {
      bubbles: true,
      cancelable: false
    });
    inputRef.current.dispatchEvent(evt);
    setValues(newValues);
    onChange(evt);
  };
  return /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: fullWidth,
    name: name,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: "normal"
  }, /*#__PURE__*/React.createElement("input", {
    defaultValue: rest.defaultValue || value,
    name: name,
    ref: inputRef,
    style: {
      height: 0,
      width: 0,
      appearance: 'none',
      border: 'none',
      outline: 'none',
      margin: 0,
      padding: 0
    }
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.container
  }, /*#__PURE__*/React.createElement(Select, {
    className: classes.select,
    disabled: disabled,
    name: name + "_hours",
    placeholder: "HH",
    required: required,
    native: true,
    options: hourOptions,
    value: values.hour,
    onChange: function onChange(e) {
      handleChange(e, 'hour');
    }
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.spacer
  }, ":"), /*#__PURE__*/React.createElement(Select, {
    className: classes.select,
    disabled: disabled,
    name: name + "_minutes",
    placeholder: "MM",
    required: required,
    native: true,
    options: minuteOptions,
    value: values.minute,
    onChange: function onChange(e) {
      handleChange(e, 'minute');
    }
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.spacer
  }), /*#__PURE__*/React.createElement(Select, {
    className: classes.select,
    disabled: disabled,
    name: name + "_meridian",
    placeholder: "AM",
    required: required,
    native: true,
    options: meridianOptions,
    value: values.meridian,
    onChange: function onChange(e) {
      handleChange(e, 'meridian');
    }
  })));
}
TimeInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the input will be not be intractive.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, will disable the first option in each select box.
   */
  disableFirstOption: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * If `true`, the component will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The time format in which `value` is provided.
   */
  fromFormat: PropTypes.string,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Placement of the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The increment value used to create minutes list.
   */
  minutesIncrementStep: PropTypes.oneOf([1, 5]),
  /**
   * Function to handle input change
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Text to describe the field.
   */
  label: PropTypes.node,
  /**
   * HTML `name` attribute of the field.
   */
  name: PropTypes.string.isRequired,
  /**
   *
   */
  required: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The size of the input.
   */
  size: PropTypes.oneOf(['medium', 'small']),
  /**
   * The time format in which new `value` will be returned after selection.
   * @see https://day.js.org/docs/en/display/format#list-of-all-available-formats
   */
  toFormat: PropTypes.string,
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.string
} : {};
TimeInput.defaultProps = {
  disabled: false,
  disableFirstOption: false,
  errorMessage: '',
  fullWidth: false,
  fromFormat: 'HH:mm:ss',
  helperText: '',
  helperTextPlacement: 'above',
  label: '',
  minutesIncrementStep: 5,
  required: false,
  showError: false,
  size: 'medium',
  toFormat: 'HH:mm:ss',
  value: ''
};
export default TimeInput;